import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Custom dropdown for comments sort (replaces native select in comments panel).
export default class extends Controller {
  static targets = ["trigger", "panel", "label"]

  static values = {
    commentableId: String,
    commentableType: String,
    loadingId: String,
    frameId: String,
  }

  connect() {
    this.boundDocClick = this.onDocumentClick.bind(this)
    this.boundKeydown = this.onKeydown.bind(this)
  }

  disconnect() {
    this.close()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.panelTarget.hidden) this.open()
    else this.close()
  }

  open() {
    if (!this.panelTarget.hidden) return
    this.panelTarget.hidden = false
    this.element.classList.add("is-open")
    this.triggerTarget.setAttribute("aria-expanded", "true")
    document.addEventListener("click", this.boundDocClick)
    document.addEventListener("keydown", this.boundKeydown)
  }

  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.hidden = true
    this.element.classList.remove("is-open")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    document.removeEventListener("click", this.boundDocClick)
    document.removeEventListener("keydown", this.boundKeydown)
  }

  onDocumentClick(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }

  onKeydown(event) {
    if (event.key === "Escape") this.close()
  }

  select(event) {
    event.preventDefault()
    const sortValue = event.currentTarget.dataset.sortValue
    if (!sortValue) return

    if (this.hasLabelTarget) this.labelTarget.textContent = event.currentTarget.textContent.trim()
    this.close()

    const url = this.buildUrl(sortValue)
    if (!url) {
      console.error("Unable to determine comments sort URL for type:", this.commentableTypeValue)
      return
    }

    this.showLoading()

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        return response.text()
      })
      .then((html) => {
        if (Turbo && typeof Turbo.renderStreamMessage === "function") {
          Turbo.renderStreamMessage(html)
        } else {
          const parser = new DOMParser()
          const doc = parser.parseFromString(html, "text/html")
          doc.querySelectorAll("turbo-stream").forEach((stream) => {
            document.body.appendChild(stream)
          })
        }
      })
      .catch((error) => {
        console.error("Error fetching sorted comments:", error)
        const frame = document.getElementById(`comments-frame-${this.commentableIdValue}`)
        if (frame) {
          frame.innerHTML = '<div class="comments-error"><p>Error loading comments. Please try again.</p></div>'
        }
      })
      .finally(() => {
        this.hideLoading()
      })
  }

  buildUrl(sortValue) {
    switch (this.commentableTypeValue) {
      case "Post":
        return `/posts/${this.commentableIdValue}/comments?sort=${sortValue}`
      case "AcademyPost":
        return `/academy_posts/${this.commentableIdValue}/comments?sort=${sortValue}`
      default:
        return `/comments?commentable_type=${encodeURIComponent(this.commentableTypeValue)}&commentable_id=${this.commentableIdValue}&sort=${sortValue}`
    }
  }

  showLoading() {
    if (this.loadingIdValue) {
      const loader = document.getElementById(this.loadingIdValue)
      if (loader) loader.classList.add("active")
    }

    const frame = this._frameElement()
    if (frame) {
      this._previousFrameDisplay = frame.style.display
      frame.style.display = "none"
    }
  }

  hideLoading() {
    if (this.loadingIdValue) {
      const loader = document.getElementById(this.loadingIdValue)
      if (loader) loader.classList.remove("active")
    }

    const frame = this._frameElement()
    if (frame) {
      frame.style.display = this._previousFrameDisplay ?? ""
      this._previousFrameDisplay = undefined
    }
  }

  _frameElement() {
    if (!this.frameIdValue) return null
    return document.getElementById(this.frameIdValue)
  }
}

